import { z } from "zod";
/**
 * Pipeline de déploiement de plugins (site → serveur) — DEPLOY_PIPELINE_SPEC.
 *
 * L'admin téléverse des JARs de plugins depuis /admin/deploiement → le BFF pré-valide et streame
 * vers Etheria_WebAPI (raw) → le serveur MC valide à nouveau (autorité), classe chaque plugin en
 * HOT-SWAP ou REDÉMARRAGE REQUIS, met en attente (staging), puis applique (hot-swap immédiat via
 * DeployEngine ou dépôt dans plugins/update pour le prochain boot).
 *
 * Ces schémas décrivent les corps ÉCHANGÉS ENTRE LE NAVIGATEUR ET LE BFF. Le BFF valide, gate le
 * rôle admin + CSRF, puis relaie vers WebAPI. Noms alignés sur le spec. La WebAPI reste l'autorité :
 * le BFF ne fait que pré-valider (défense en profondeur) et streamer.
 */
/**
 * Allowlist du nom de fichier JAR (DEPLOY_PIPELINE_SPEC §Site + §Safety) : `Etheria_<Alnum_>.jar`.
 * Utilisé côté BFF (pré-validation) ET côté web (feedback immédiat avant upload).
 */
export const DEPLOY_JAR_NAME_RE = /^Etheria_[A-Za-z0-9_]+\.jar$/;
/** Taille maximale d'un JAR téléversé (30 Mo — cf. `limits.max-upload-bytes` WebAPI). */
export const DEPLOY_MAX_UPLOAD_BYTES = 30 * 1024 * 1024;
/**
 * Décision de déploiement calculée par la WebAPI (autorité) à partir de la classification statique.
 * - `hotswap`  : le plugin peut être rechargé à chaud (feuille/gris, rien ne le HARD-dépend).
 * - `restart`  : le plugin doit être mis en attente et appliqué au prochain redémarrage (core /
 *                fournisseur de service / dépendu, ou self : Core/Deploy/WebAPI).
 */
export const DeployDecisionSchema = z.enum(["hotswap", "restart"]);
/**
 * Classe statique d'un plugin (DEPLOY_PIPELINE_SPEC §Classification). Sert au badge + à l'explication.
 * - `leaf`     : feuille — rien ne dépend d'elle → hot-swap sûr.
 * - `grey`     : gris — dépendances souples résolues par requête → hot-swap sûr.
 * - `provider` : fournit un service consommé par d'autres → doit redémarrer.
 * - `depended` : d'autres plugins le HARD-dépendent → doit redémarrer.
 * - `core`     : Etheria_Core (ou self : Deploy/WebAPI) → jamais de hot-swap.
 */
export const DeployClassificationSchema = z.enum(["leaf", "grey", "provider", "depended", "core"]);
/**
 * Un plugin Etheria chargé sur le serveur (GET /api/admin/deploy/plugins → proxy WebAPI R
 * /api/read/plugins). Inclut la classification statique + l'état de mise en attente (staged).
 */
export const DeployPluginSchema = z.object({
    /** Nom canonique du plugin (== nom dans plugin.yml), ex "Etheria_Shop". */
    name: z.string(),
    /** Version déclarée (plugin.yml), si connue. */
    version: z.string().nullable().optional(),
    /** true si le plugin est actuellement activé (enabled) sur le serveur. */
    enabled: z.boolean(),
    /** Classe statique (leaf/grey/provider/depended/core). */
    classification: DeployClassificationSchema,
    /** Décision qui s'appliquerait à un nouveau JAR de ce plugin. */
    decision: DeployDecisionSchema,
    /** true si un JAR est déjà en attente (staged) pour ce plugin (prêt à appliquer). */
    staged: z.boolean().optional(),
});
/**
 * Réponse GET /api/admin/deploy/plugins.
 * `available:false` = WebAPI non configurée/injoignable (dégradation gracieuse — l'UI affiche l'état
 * « serveur non connecté » au lieu d'un crash). `health` reflète webapi.health() côté BFF.
 */
export const DeployPluginsResSchema = z.object({
    available: z.boolean(),
    /** Santé de la WebAPI (webapi.health()) — pilote le bandeau d'état live de la page. */
    health: z.boolean(),
    plugins: z.array(DeployPluginSchema),
});
/**
 * Résultat d'un upload de JAR (POST /api/admin/deploy/upload → WebAPI raw). La WebAPI a validé
 * (ZIP/plugin.yml/allowlist/taille), vérifié le sha256, sauvegardé l'ancien JAR et mis en attente.
 */
export const DeployUploadResSchema = z.object({
    ok: z.boolean(),
    /** Nom canonique du plugin extrait du JAR validé (source d'autorité : plugin.yml). */
    name: z.string(),
    /** Taille du JAR reçu (octets). */
    size: z.number().int().nonnegative(),
    /** sha256 (hex) recalculé et vérifié par la WebAPI. */
    sha256: z.string(),
    /** Classe statique du plugin. */
    classification: DeployClassificationSchema,
    /** Décision : hot-swap possible ou redémarrage requis. */
    decision: DeployDecisionSchema,
    /** Message FR optionnel (ex avertissement). */
    message: z.string().optional(),
});
/**
 * Métadonnées d'upload passées en query string à la route BFF (le corps est le flux binaire du JAR).
 * Le BFF pré-valide `name` (allowlist) + `sha256` (hex 64) avant de streamer vers la WebAPI.
 */
export const DeployUploadMetaSchema = z.object({
    /** Nom de fichier du JAR — DOIT matcher `^Etheria_[A-Za-z0-9_]+\.jar$`. */
    name: z.string().regex(DEPLOY_JAR_NAME_RE, "Nom de fichier JAR invalide (Etheria_X.jar attendu)."),
    /** sha256 hex minuscule (64 caractères) calculé côté navigateur pour vérification bout-à-bout. */
    sha256: z.string().regex(/^[0-9a-f]{64}$/, "sha256 hexadécimal (64) requis."),
});
/**
 * Corps POST /api/admin/deploy/validate — dry-run (simulation). `names` restreint le plan aux
 * plugins mis en attente concernés (vide = tous les JARs staged). `includeCore` reste faux
 * (Core ne se hot-swap jamais ; il transite par plugins/update).
 */
export const DeployValidateReqSchema = z.object({
    names: z.array(z.string()).optional(),
    includeCore: z.boolean().optional(),
});
/** Une entrée du plan de simulation (par plugin). */
export const DeployPlanEntrySchema = z.object({
    name: z.string(),
    decision: DeployDecisionSchema,
    classification: DeployClassificationSchema,
    /** Action concrète qui sera prise ("reload", "add", "stage-update", "skip"…). */
    action: z.string().optional(),
    /** Explication FR courte (pourquoi hot-swap vs redémarrage). */
    reason: z.string().optional(),
});
/**
 * Réponse POST /api/admin/deploy/validate — plan de simulation (aucune mutation).
 * `restartRequired` = au moins un plugin doit passer par un redémarrage.
 */
export const DeployValidateResSchema = z.object({
    ok: z.boolean(),
    /** Ordre topologique de rechargement (Kahn) tel que calculé par DeployEngine.scan. */
    order: z.array(z.string()).optional(),
    /** Plan détaillé par plugin. */
    plan: z.array(DeployPlanEntrySchema),
    /** true si au moins une entrée nécessite un redémarrage. */
    restartRequired: z.boolean(),
    /** true si un déploiement est déjà en cours (single-flight DeployEngine → 409 à l'apply). */
    running: z.boolean().optional(),
});
/**
 * Mode d'application (POST /api/admin/deploy/apply) :
 * - `stage`   : dépose dans plugins/update → tout au prochain boot. Mode live obligatoire.
 * - `auto`/`hotswap` restent parsés pour compat ancienne UI/API, mais le BFF live force `stage`.
 */
export const DeployApplyModeSchema = z.enum(["auto", "hotswap", "stage"]);
/** Corps POST /api/admin/deploy/apply. `names` cible les plugins staged ; vide = tous. */
export const DeployApplyReqSchema = z.object({
    names: z.array(z.string()).optional(),
    mode: DeployApplyModeSchema.default("stage"),
});
/** Résultat par plugin après application. */
export const DeployApplyEntrySchema = z.object({
    name: z.string(),
    decision: DeployDecisionSchema,
    /** État final ("hotswapped", "staged-update", "failed", "skipped"). */
    status: z.string(),
    message: z.string().optional(),
});
/**
 * Réponse POST /api/admin/deploy/apply.
 * `restartRequired:true` → l'UI affiche le bandeau « REDÉMARRAGE REQUIS » + bouton de redémarrage
 * confirmé. Le redémarrage n'est JAMAIS implicite dans l'apply (DEPLOY_PIPELINE_SPEC §Safety).
 */
export const DeployApplyResSchema = z.object({
    ok: z.boolean(),
    results: z.array(DeployApplyEntrySchema),
    /** true si au moins un plugin a été déposé dans plugins/update (attend un redémarrage). */
    restartRequired: z.boolean(),
    message: z.string().optional(),
});
/**
 * Corps POST /api/admin/deploy/restart. `confirm` DOIT valoir true (garde-fou double : bouton
 * confirmé côté UI + drapeau explicite côté BFF). Un redémarrage peut laisser le serveur hors-ligne
 * si l'hôte ne redémarre pas automatiquement — la WebAPI avertit.
 */
export const DeployRestartReqSchema = z.object({
    confirm: z.literal(true),
});
/** Réponse POST /api/admin/deploy/restart. */
export const DeployRestartResSchema = z.object({
    ok: z.boolean(),
    /** true si un arrêt/redémarrage a effectivement été déclenché (sinon = instruction manuelle). */
    triggered: z.boolean(),
    message: z.string().optional(),
});
//# sourceMappingURL=deploy.js.map